/**
 * Diccionario cerrado de `kind` (tabla, tile y `GET /getreferenceplacekinds`).
 * El filtro del mapa usa este slug, nunca el label en español.
 *
 * Superficies: `/app` ve el catálogo completo (due diligence). El share
 * muestra amenities de entorno; no seguridad (`police`, `prison`), parque
 * nacional (`national_park`) ni basurales (`landfill`).
 *
 * Conteos DEV: snapshot 11 sep 2026. `gym` OSM reemplazó Poblaciones.
 * `national_park` está semillado sin filas.
 */

export const REFERENCE_PLACE_SURFACE_APP = 'app';
export const REFERENCE_PLACE_SURFACE_SHARE = 'share';

export const REFERENCE_PLACE_CATEGORY_LABELS = {
  education: 'Educación',
  transport: 'Transporte',
  health: 'Salud',
  worship: 'Culto',
  nature: 'Naturaleza',
  security: 'Seguridad',
  culture: 'Cultura',
  recreation: 'Recreación',
  commerce: 'Comercio',
  environment: 'Ambiente',
};

/**
 * @typedef {{
 *   kind: string,
 *   category: string,
 *   label: string,
 *   share?: boolean,
 * }} ReferencePlaceKindEntry
 */

/** @type {ReferencePlaceKindEntry[]} */
export const REFERENCE_PLACE_KIND_DICTIONARY = [
  { kind: 'school', category: 'education', label: 'Establecimiento educativo' },
  { kind: 'hospital', category: 'health', label: 'Centro de salud' },
  { kind: 'park', category: 'nature', label: 'Espacio verde / plaza' },
  { kind: 'supermarket', category: 'commerce', label: 'Supermercado' },
  { kind: 'mall', category: 'commerce', label: 'Centro comercial' },
  { kind: 'gym', category: 'recreation', label: 'Gimnasio' },
  { kind: 'sports', category: 'recreation', label: 'Establecimiento deportivo' },
  { kind: 'airport', category: 'transport', label: 'Aeropuerto' },
  { kind: 'church', category: 'worship', label: 'Iglesia / culto' },
  { kind: 'police', category: 'security', label: 'Dependencia policial', share: false },
  { kind: 'prison', category: 'security', label: 'Establecimiento penitenciario', share: false },
  { kind: 'library', category: 'culture', label: 'Librería / biblioteca' },
  { kind: 'museum', category: 'culture', label: 'Museo' },
  { kind: 'monument', category: 'culture', label: 'Monumento / lugar histórico' },
  { kind: 'cultural_org', category: 'culture', label: 'Organización cultural' },
  { kind: 'national_park', category: 'nature', label: 'Parque nacional', share: false },
  { kind: 'landfill', category: 'environment', label: 'Basural / relleno', share: false },
];

export const REFERENCE_PLACE_DICTIONARY_KINDS = REFERENCE_PLACE_KIND_DICTIONARY
  .map((entry) => entry.kind);

const KIND_BY_SLUG = new Map(
  REFERENCE_PLACE_KIND_DICTIONARY.map((entry) => [entry.kind, entry]),
);

/**
 * @param {string} category
 * @returns {string}
 */
export function referencePlaceCategoryLabel(category) {
  const key = String(category || '').trim();
  return REFERENCE_PLACE_CATEGORY_LABELS[key] || '';
}

/**
 * Label UI del kind. Fallback si el catálogo no trajo etiqueta.
 *
 * @param {string} kind
 * @returns {string}
 */
export function referencePlaceKindLabel(kind) {
  const entry = KIND_BY_SLUG.get(String(kind || '').trim());
  return entry?.label || '';
}

/**
 * `/app` muestra todo. Share: amenities; un kind desconocido no se ofrece.
 *
 * @param {string} kind
 * @param {string} [surface]
 * @returns {boolean}
 */
export function referencePlaceKindAllowedOnSurface(kind, surface = REFERENCE_PLACE_SURFACE_SHARE) {
  const key = String(kind || '').trim();
  if (!key) return false;
  if (surface === REFERENCE_PLACE_SURFACE_APP) return true;
  const entry = KIND_BY_SLUG.get(key);
  if (!entry) return false;
  return entry.share !== false;
}

/**
 * @param {Array<{ kind: string }>} kinds
 * @param {string} [surface]
 * @returns {Array}
 */
export function filterReferencePlaceKindsForSurface(
  kinds,
  surface = REFERENCE_PLACE_SURFACE_SHARE,
) {
  return (Array.isArray(kinds) ? kinds : []).filter((entry) => (
    referencePlaceKindAllowedOnSurface(entry?.kind, surface)
  ));
}
